import axios from "axios";
import { hc } from "@/lib/heycatch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export { BACKEND_URL };
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 120000,
  withCredentials: true,
});

export const fetchSections = async () => {
  const { data } = await api.get("/sections");
  return data.sections;
};

export const fetchSection = async (id) => {
  const { data } = await api.get(`/sections/${id}`);
  return data;
};

export const fetchEntries = async (sectionId) => {
  const params = sectionId ? { section_id: sectionId } : {};
  const { data } = await api.get("/entries", { params });
  return data;
};

export const createEntry = async (payload) => {
  const { data } = await api.post("/entries", payload);
  return data;
};

export const deleteEntry = async (id) => {
  const { data } = await api.delete(`/entries/${id}`);
  return data;
};

export const transcribeAudio = async (blob) => {
  const form = new FormData();
  const file = new File([blob], "recording.webm", { type: blob.type || "audio/webm" });
  form.append("audio", file);
  const { data } = await api.post("/transcribe", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.text;
};

// ---------- No-account Chapter One preview ----------
export const fetchPreviewChapter = async () => {
  const { data } = await api.get("/preview/chapter-one");
  return data;
};

export const transcribePreviewAudio = async (blob) => {
  const form = new FormData();
  form.append("audio", new File([blob], "recording.webm", { type: blob.type || "audio/webm" }));
  const { data } = await api.post("/preview/transcribe", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.text;
};

export const requestPreviewReflection = async (promptId, answer) => {
  const { data } = await api.post("/preview/reflect", { prompt_id: promptId, answer });
  hc("trackEvent", "mirror_heard", { surface: "try", prompt_id: promptId });
  return data;
};

export const requestReflection = async (promptText, answer, meta = {}) => {
  const { data } = await api.post("/reflect", {
    prompt_text: promptText,
    answer,
    section_id: meta.sectionId,
    prompt_id: meta.promptId,
  });
  hc("trackEvent", "mirror_heard", { surface: "workbook", section_id: meta.sectionId, prompt_id: meta.promptId });
  return data; // { reflection, distress }
};

export const sendMirrorTurn = async (entryId, message) => {
  const { data } = await api.post("/mirror/turn", { entry_id: entryId, message });
  return data; // { user_turn, mirror_turn, remaining_today, closed, distress }
};

export const fetchMirrorState = async (entryId) => {
  const { data } = await api.get(`/mirror/state/${entryId}`);
  return data; // { entry_id, turns, remaining_today, closed }
};

export const synthesizeSpeech = async (text, voice) => {
  try {
    const body = voice ? { text, voice } : { text };
    const { data } = await api.post(
      "/tts",
      body,
      { responseType: "blob" }
    );
    return URL.createObjectURL(data);
  } catch (err) {
    // With responseType:"blob" the error body is a Blob — parse it back to JSON
    // so callers can read err.response.data.detail like normal.
    const blob = err?.response?.data;
    if (blob && typeof blob.text === "function") {
      try {
        const raw = await blob.text();
        err.response.data = JSON.parse(raw);
      } catch {
        // leave as-is if not JSON
      }
    }
    throw err;
  }
};

export const fetchVoices = async () => {
  const { data } = await api.get("/voices");
  return data;
};

export const fetchEntitlements = async () => {
  const { data } = await api.get("/entitlements");
  return data;
};

export const fetchCatalog = async () => {
  const { data } = await api.get("/payments/catalog");
  return data;
};

export const createCheckout = async (lookupKey) => {
  hc("trackEvent", "checkout_started", { plan: lookupKey, page: window.location.pathname });
  const { data } = await api.post("/payments/checkout", {
    lookup_key: lookupKey,
    origin_url: window.location.origin,
  });
  return data;
};

export const getPaymentStatus = async (sessionId) => {
  const { data } = await api.get(`/payments/status/${sessionId}`);
  return data;
};

export const openBillingPortal = async () => {
  const { data } = await api.post("/payments/portal", {
    return_url: `${window.location.origin}/dashboard`,
  });
  return data.portal_url;
};

export const restorePurchases = async () => {
  const { data } = await api.post("/payments/restore");
  return data;
};

export const createGift = async ({ section_id, recipient_email, note }) => {
  const { data } = await api.post("/gifts", {
    section_id,
    recipient_email,
    note: note || null,
    origin_url: window.location.origin,
  });
  return data.gift;
};

export const fetchGiftInbox = async () => {
  const { data } = await api.get("/gifts/inbox");
  return data.gifts;
};

export const claimGift = async (giftId) => {
  const { data } = await api.post(`/gifts/${giftId}/claim`);
  return data;
};

export const fetchReferralStatus = async () => {
  const { data } = await api.get("/referral/status");
  return data;
};

export const pauseSubscription = async (months = 1) => {
  const { data } = await api.post("/payments/pause", { months });
  return data;
};

export const fetchFounders = async () => {
  const { data } = await api.get("/founders");
  return data;
};

export const fetchFoundersCap = async () => {
  const { data } = await api.get("/founders/cap");
  return data;
};

export const fetchVoiceOptions = async () => {
  const { data } = await api.get("/user/voice-options");
  return data;
};

export const saveVoicePreference = async (preferred_voice) => {
  const { data } = await api.put("/user/voice-preference", { preferred_voice });
  return data;
};

export const refundSubscription = async () => {
  // Legacy shim — refund flow was retired Phase 13. New callers should use cancelSubscription.
  return cancelSubscription();
};

export const cancelSubscription = async () => {
  const { data } = await api.post("/payments/cancel");
  return data;
};

export const fetchStreak = async () => {
  const { data } = await api.get("/streak");
  return data;
};

export const fetchSeason = async () => {
  const { data } = await api.get("/season");
  return data;
};

export const fetchAnniversaryStatus = async () => {
  const { data } = await api.get("/anniversary/status");
  return data;
};

export const fetchAnniversaryPortrait = async () => {
  const { data } = await api.get("/anniversary/portrait");
  return data;
};

export const fetchWhisperCircle = async () => {
  const { data } = await api.get("/whisper/circle");
  return data;
};

export const fetchRerun = async () => {
  const { data } = await api.get("/rituals/rerun");
  return data;
};

export const triggerEveningReturn = async () => {
  const { data } = await api.post("/rituals/evening-return/trigger");
  return data;
};

// ---------- Admin ----------
export const fetchDistressEvents = async (params = {}) => {
  const { data } = await api.get("/admin/distress-events", { params });
  return data;
};
export const resolveDistressEvent = async (eventId) => {
  const { data } = await api.post(`/admin/distress-events/${eventId}/resolve`);
  return data;
};
export const fetchAdminAnalytics = async (days = 30) => {
  const { data } = await api.get("/admin/analytics/traffic", { params: { days } });
  return data;
};
export const fetchAdminAnnouncements = async () => {
  const { data } = await api.get("/admin/announcements");
  return data;
};
export const createAnnouncement = async (payload) => {
  const { data } = await api.post("/admin/announcements", payload);
  return data;
};
export const archiveAnnouncement = async (annId) => {
  const { data } = await api.post(`/admin/announcements/${annId}/archive`);
  return data;
};

// ---------- User-scoped announcements ----------
export const fetchActiveAnnouncements = async () => {
  const { data } = await api.get("/announcements/active");
  return data;
};
export const dismissAnnouncement = async (annId) => {
  const { data } = await api.post(`/announcements/${annId}/dismiss`);
  return data;
};

// ---------- Affiliate ----------
export const fetchMyReferral = async () => {
  const { data } = await api.get("/referral/me");
  return data;
};
export const fetchAdminAffiliates = async () => {
  const { data } = await api.get("/admin/affiliates");
  return data;
};

// ---------- Admin Pricing ----------
export const fetchAdminPricing = async () => {
  const { data } = await api.get("/admin/pricing/tiers");
  return data;
};
export const changeStripePrice = async ({ lookup_key, new_amount_cents, confirm_phrase }) => {
  const { data } = await api.post("/admin/pricing/create", {
    lookup_key,
    new_amount_cents,
    confirm_phrase,
  });
  return data;
};

// ---------- Admin Providers ----------
export const fetchProvidersStatus = async () => {
  const { data } = await api.get("/admin/providers/status");
  return data;
};

export const fetchProvidersHealth = async () => {
  const { data } = await api.get("/admin/providers/health");
  return data;
};

export const fetchProvidersCosts = async () => {
  const { data } = await api.get("/admin/providers/costs");
  return data;
};

export const fetchProvidersCostHistory = async () => {
  const { data } = await api.get("/admin/providers/costs/history");
  return data;
};

export const fetchProvidersMargin = async () => {
  const { data } = await api.get("/admin/providers/margin");
  return data;
};

// ---------- Keepsake Book ----------
export const fetchKeepsakeManuscript = async () => {
  const { data } = await api.get("/keepsake/manuscript");
  return data;
};

export const submitPrintRequest = async (shipping) => {
  const { data } = await api.post("/keepsake/print-request", shipping);
  return data;
};

export const fetchKeepsakeProducts = async () => {
  const { data } = await api.get("/keepsake/products");
  return data;
};

export const fetchKeepsakeEstimate = async (payload) => {
  const { data } = await api.post("/keepsake/estimate", payload);
  return data;
};

export const fetchKeepsakeShippingOptions = async (payload) => {
  const { data } = await api.post("/keepsake/shipping-options", payload);
  return data;
};

export const createKeepsakeCheckout = async (payload) => {
  hc("trackEvent", "checkout_started", { plan: "keepsake_book", page: window.location.pathname });
  const { data } = await api.post("/keepsake/order/checkout", payload);
  return data;
};

export const fetchKeepsakeOrder = async (orderId) => {
  const { data } = await api.get(`/keepsake/order/${orderId}`);
  return data;
};

export const fetchKeepsakeOrders = async () => {
  const { data } = await api.get("/keepsake/orders");
  return data;
};

// ---------- Admin Founders + Cancel-Save ----------
export const fetchAdminFounders = async () => {
  const { data } = await api.get("/admin/founders");
  return data;
};

export const fetchCancelSaveAnalytics = async () => {
  const { data } = await api.get("/admin/analytics/cancel-save");
  return data;
};

// ---------- Admin Marketing ----------
export const fetchNonSubscribers = async ({ q = "", segment = "", limit = 200, skip = 0 } = {}) => {
  const params = { limit, skip };
  if (q) params.q = q;
  if (segment) params.segment = segment;
  const { data } = await api.get("/admin/marketing/non-subscribers", { params });
  return data;
};

export const nonSubscribersCsvUrl = (segment = "") => {
  const base = `${API}/admin/marketing/non-subscribers.csv`;
  return segment ? `${base}?segment=${encodeURIComponent(segment)}` : base;
};

// ---------- Admin Broadcasts ----------
export const fetchBroadcastCampaigns = async () => {
  const { data } = await api.get("/admin/broadcast/campaigns");
  return data;
};
export const fetchBroadcastPreview = async (campaignId) => {
  const { data } = await api.get(`/admin/broadcast/preview/${campaignId}`);
  return data;
};
export const sendBroadcast = async ({ campaign_id, confirm_phrase, segments }) => {
  const { data } = await api.post("/admin/broadcast/send", {
    campaign_id,
    confirm_phrase,
    segments: segments || null,
  });
  return data;
};

export const previewBroadcastAudience = async ({ campaign_id, segments }) => {
  const { data } = await api.post("/admin/broadcast/audience-preview", {
    campaign_id,
    confirm_phrase: "preview",  // ignored by preview endpoint
    segments: segments || null,
  });
  return data;
};

// Trial nudge (day-5) — auto-audience, no segments to pick.
export const fetchTrialNudgePreview = async () => {
  const { data } = await api.get("/admin/broadcast/trial-nudge/preview");
  return data;
};

export const sendTrialNudge = async () => {
  const { data } = await api.post("/admin/broadcast/trial-nudge/send", {
    confirm_phrase: "send",
  });
  return data;
};


export const optInWhisper = async (optIn = true) => {
  const { data } = await api.post("/whisper/optin", { opt_in: optIn });
  return data;
};

export const submitWhisperReflection = async (text) => {
  const { data } = await api.post("/whisper/reflection", { text });
  return data;
};

export const updateEntry = async (id, patch) => {
  const { data } = await api.patch(`/entries/${id}`, patch);
  return data;
};


// ---------- Ebook library ----------
export const fetchLibrary = async () => {
  const { data } = await api.get("/library");
  return data;
};

export const downloadEbook = async (ebookId, title) => {
  const res = await api.get(`/library/${ebookId}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(title || "ebook").replace(/\s+/g, "_")}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const fetchAdminEbooks = async () => {
  const { data } = await api.get("/admin/ebooks");
  return data;
};

export const uploadEbook = async (form) => {
  const { data } = await api.post("/admin/ebooks", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const deleteEbook = async (id) => {
  const { data } = await api.delete(`/admin/ebooks/${id}`);
  return data;
};

export const updateEbook = async (id, formData) => {
  const { data } = await api.put(`/admin/ebooks/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};


// ---------- Coupons (admin dashboard card) ----------
export const fetchAdminCoupons = async () => {
  const { data } = await api.get("/admin/coupons");
  return data;
};


// ---------- Partner program (paid affiliates) ----------
export const fetchPartnerMe = async () => {
  const { data } = await api.get("/partner/me");
  return data;
};

export const acceptPartnerAgreement = async ({ typed_name, version }) => {
  const { data } = await api.post("/partner/agreement/accept", { typed_name, version });
  return data;
};

// ---- Admin ----
export const fetchAdminPartners = async () => {
  const { data } = await api.get("/admin/partners");
  return data;
};

export const fetchAdminPartnerDetail = async (userId) => {
  const { data } = await api.get(`/admin/partners/${userId}`);
  return data;
};

export const promotePartner = async ({ user_id, code, terms }) => {
  const { data } = await api.post("/admin/partners", { user_id, code, terms });
  return data;
};

export const updatePartnerTerms = async (userId, patch) => {
  const { data } = await api.put(`/admin/partners/${userId}/terms`, patch);
  return data;
};

export const generatePartnerAgreement = async (userId) => {
  const { data } = await api.post(`/admin/partners/${userId}/agreement/generate`);
  return data;
};

export const runPartnerAudit = async (userId, month) => {
  const { data } = await api.get(`/admin/partners/${userId}/audit`, {
    params: month ? { month } : {},
  });
  return data;
};

export const recordPartnerPayout = async (userId, { commission_ids, method, notes }) => {
  const { data } = await api.post(`/admin/partners/${userId}/payouts`, {
    commission_ids,
    method,
    notes,
  });
  return data;
};

export const deactivatePartner = async (userId) => {
  const { data } = await api.post(`/admin/partners/${userId}/deactivate`);
  return data;
};

// ---------- Partner payout portal ----------
export const setPartnerPayoutMethod = async ({ method, handle }) => {
  const { data } = await api.put("/partner/payout-method", { method, handle });
  return data;
};

export const requestPartnerPayout = async ({ notes } = {}) => {
  const { data } = await api.post("/partner/payout-request", { notes: notes || null });
  return data;
};

export const fetchAdminPayoutRequests = async (status) => {
  const { data } = await api.get("/admin/partner-payout-requests", {
    params: status ? { status } : {},
  });
  return data;
};

export const approvePayoutRequest = async (requestId) => {
  const { data } = await api.post(`/admin/partner-payout-requests/${requestId}/approve`);
  return data;
};

export const rejectPayoutRequest = async (requestId) => {
  const { data } = await api.post(`/admin/partner-payout-requests/${requestId}/reject`);
  return data;
};


export const adminSearchUsers = async (q, limit = 20) => {
  const { data } = await api.get("/admin/users/search", { params: { q, limit } });
  return data;
};


// ---------- Partner testimonial (First Prompt Widget) ----------
export const fetchTestimonialCandidates = async () => {
  const { data } = await api.get("/partner/testimonial/candidates");
  return data;
};

export const setPartnerTestimonial = async (entryId) => {
  const { data } = await api.put("/partner/testimonial", { entry_id: entryId });
  return data;
};

export const clearPartnerTestimonial = async () => {
  const { data } = await api.delete("/partner/testimonial");
  return data;
};

// Public — no auth. Called from Landing when ?ref=CODE is present.
export const fetchPublicPartnerProfile = async (code) => {
  const { data } = await api.get(`/partner-profile/${encodeURIComponent(code)}`);
  return data;
};



// ---------- Blog (self-hosted, SEO) ----------
export const fetchBlogPosts = async (params = {}) => {
  const { data } = await api.get("/blog/posts", { params });
  return data;
};

export const fetchBlogPost = async (slug, { preview = false } = {}) => {
  const path = preview
    ? `/blog/preview/${encodeURIComponent(slug)}`
    : `/blog/posts/${encodeURIComponent(slug)}`;
  const { data } = await api.get(path);
  return data;
};

export const fetchBlogSharePreview = async (slug, token) => {
  const { data } = await api.get(`/blog/share-preview/${encodeURIComponent(slug)}`, {
    params: { token },
  });
  return data;
};

export const mintBlogPreviewLink = async (postId, { ttl_hours = 72, label } = {}) => {
  const form = new FormData();
  form.append("ttl_hours", String(ttl_hours));
  if (label) form.append("label", label);
  const { data } = await api.post(`/admin/blog/posts/${postId}/preview-link`, form);
  return data;
};

export const listBlogPreviewLinks = async (postId) => {
  const { data } = await api.get(`/admin/blog/posts/${postId}/preview-links`);
  return data;
};

export const revokeBlogPreviewLink = async (linkId) => {
  const { data } = await api.delete(`/admin/blog/preview-links/${linkId}`);
  return data;
};

export const fetchBlogCategories = async () => {
  const { data } = await api.get("/blog/categories");
  return data.categories || [];
};

export const fetchBlogTags = async () => {
  const { data } = await api.get("/blog/tags");
  return data.tags || [];
};

export const blogHeroUrl = (postId) => `${API}/blog/hero/${postId}`;

// Admin
export const fetchAdminBlogPosts = async () => {
  const { data } = await api.get("/admin/blog/posts");
  return data;
};

export const fetchAdminBlogPost = async (id) => {
  const { data } = await api.get(`/admin/blog/posts/${id}`);
  return data;
};

export const createBlogPost = async (form) => {
  const { data } = await api.post("/admin/blog/posts", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const updateBlogPost = async (id, form) => {
  const { data } = await api.put(`/admin/blog/posts/${id}`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const deleteBlogPost = async (id) => {
  const { data } = await api.delete(`/admin/blog/posts/${id}`);
  return data;
};

// ---------- Blog auto-agent ----------
export const fetchBlogAgentStatus = async () => {
  const { data } = await api.get("/admin/blog/agent/status");
  return data;
};

export const runBlogAgentNow = async (topic) => {
  const { data } = await api.post("/admin/blog/agent/run", null, { params: topic ? { topic } : {} });
  return data;
};

// ---------- Newsletter (blog lead capture) ----------
export const subscribeNewsletter = async ({ email, source = "blog", post_slug = null }) => {
  const { data } = await api.post("/newsletter/subscribe", { email, source, post_slug });
  return data;
};

// ---------- Blog A/B experiment ----------
export const recordExperimentEvent = async ({ experiment = "paywall_length", variant, event, session_id, post_slug = null }) => {
  try {
    const { data } = await api.post("/blog/experiments/event", {
      experiment, variant, event, session_id, post_slug,
    });
    return data;
  } catch {
    // Analytics must never crash the reader path.
    return { ok: false };
  }
};

export const fetchPaywallResults = async () => {
  const { data } = await api.get("/admin/blog/experiments/paywall/results");
  return data;
};

// ---------- AI-draft review queue ----------
export const fetchPendingBlogPosts = async () => {
  const { data } = await api.get("/admin/blog/pending");
  return data;
};

export const approveBlogPost = async (id) => {
  const { data } = await api.post(`/admin/blog/posts/${id}/approve`);
  return data;
};

export const rejectBlogPost = async (id) => {
  const { data } = await api.post(`/admin/blog/posts/${id}/reject`);
  return data;
};

// ---------- Newsletter nudge admin ----------
export const fetchNudgeStatus = async () => {
  const { data } = await api.get("/admin/newsletter/nudge/status");
  return data;
};

export const runNudgeBatch = async () => {
  const { data } = await api.post("/admin/newsletter/nudge/run");
  return data;
};

export const fetchNudgeResults = async () => {
  const { data } = await api.get("/admin/newsletter/nudge/results");
  return data;
};

// ---------- Blog comments ----------
export const fetchBlogComments = async (slug) => {
  const { data } = await api.get("/blog/comments", { params: { slug } });
  return data;
};

export const submitBlogComment = async ({ post_slug, author_name, author_email, body, rating = null, company = "" }) => {
  const { data } = await api.post("/blog/comments", {
    post_slug, author_name, author_email, body, rating, company,
  });
  return data;
};

export const fetchAdminComments = async (status = null) => {
  const params = status ? { status } : {};
  const { data } = await api.get("/admin/blog/comments", { params });
  return data;
};

export const approveComment = async (id) => {
  const { data } = await api.post(`/admin/blog/comments/${id}/approve`);
  return data;
};

export const rejectComment = async (id) => {
  const { data } = await api.post(`/admin/blog/comments/${id}/reject`);
  return data;
};

// ---------- Category landing ----------
export const fetchCategoryLanding = async (slug) => {
  const { data } = await api.get(`/blog/category/${encodeURIComponent(slug)}`);
  return data;
};

export const categoryHeroUrl = (slug) => `${API}/blog/category-hero/${slug}`;

export const fetchAdminCategories = async () => {
  const { data } = await api.get("/admin/blog/categories");
  return data;
};

export const updateCategory = async (slug, form) => {
  const { data } = await api.put(`/admin/blog/categories/${encodeURIComponent(slug)}`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

// ---------- Free ebook lead-magnet ----------
export const fetchLeadMagnet = async () => {
  const { data } = await api.get("/ebooks/lead-magnet");
  return data.ebook;
};

export const claimLeadMagnet = async ({ email, name, source = "lead-magnet" }) => {
  const { data } = await api.post("/ebooks/lead-magnet/claim", { email, name, source });
  return data;
};

export const fetchLeadMagnetStats = async () => {
  const { data } = await api.get("/admin/ebooks/lead-magnet/stats");
  return data;
};

export const setEbookLeadMagnet = async (ebookId) => {
  const { data } = await api.post(`/admin/ebooks/${ebookId}/lead-magnet`);
  return data;
};

export const clearEbookLeadMagnet = async (ebookId) => {
  const { data } = await api.delete(`/admin/ebooks/${ebookId}/lead-magnet`);
  return data;
};

// ---------- Ebook covers (Nano Banana) ----------
export const ebookCoverUrl = (ebookId) => `${API}/ebook/cover/${ebookId}`;

export const generateEbookCover = async (ebookId) => {
  const { data } = await api.post(`/admin/ebooks/${ebookId}/cover/generate`);
  return data;
};

export const backfillEbookCovers = async () => {
  const { data } = await api.post(`/admin/ebooks/covers/backfill`);
  return data;
};

// ---------- Social post kit (admin) ----------
export const fetchSocialKit = async (ebookId) => {
  const { data } = await api.get(`/admin/ebooks/${ebookId}/social-kit`);
  return data.kit;
};

export const generateSocialKit = async (ebookId) => {
  const { data } = await api.post(`/admin/ebooks/${ebookId}/social-kit/generate`);
  return data.kit;
};

export const socialKitImageUrl = (ebookId) => `${API}/social-kit/image/${ebookId}`;

// ---------- Blog OG cards ----------
export const blogOgUrl = (postId) => `${API}/blog/og/${postId}`;

export const generateBlogOgCard = async (postId) => {
  const { data } = await api.post(`/admin/blog/posts/${postId}/og-card`);
  return data;
};

export const backfillBlogOgCards = async () => {
  const { data } = await api.post(`/admin/blog/og/backfill`);
  return data;
};

// ---------- Blog comment ratings (SEO stars) ----------
export const fetchBlogAggregateRating = async (slug) => {
  const { data } = await api.get("/blog/comments/aggregate", { params: { slug } });
  return data.aggregate;
};

export const fetchAdminRatingsSummary = async () => {
  const { data } = await api.get("/admin/blog/ratings/summary");
  return data.summary || [];
};

// ---------- Featured landing-page testimonials ----------
export const fetchFeaturedComments = async (limit = 8) => {
  const { data } = await api.get("/blog/comments/featured", { params: { limit } });
  return data;
};

export const toggleFeatureComment = async (id) => {
  const { data } = await api.post(`/admin/blog/comments/${id}/feature`);
  return data;
};
